import React, { useEffect, useState, useRef } from 'react'
import { Link, useParams } from 'react-router-dom'

import Loading from '@/shared/Loading'
import useQuery from '@/hooks/useQuery'
import CohortRow from '@/shared/CohortRow'
import EventRow from '@/shared/EventRow'
import PrimaryButton from '@/shared/Buttons/Primary'
import { useGlobalState } from '@/state'
import { formatInTimezone } from '@/utils/date'

const SECTION_CLASS = 'bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xl shadow-slate-900/[0.07] p-6 sm:p-8'
const H2_CLASS = 'text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100'

const ClockIcon = (props) => (
  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z" stroke="currentColor" strokeWidth="1.2" strokeMiterlimit="10" />
    <path d="M8 4.5V8H11.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const LessonRow = ({lesson, currentUser}) => {
  const isDraft = lesson.publication_status === 'draft'
  const dayOfWeek = formatInTimezone(lesson.publish_at_utc, currentUser.timezone, 'EEE')
  const dayOfMonth = formatInTimezone(lesson.publish_at_utc, currentUser.timezone, 'd')
  const publishedAt = formatInTimezone(lesson.publish_at_utc, currentUser.timezone, 'EEE MMMM d, Y @ h:mmaaa zzz')

  let statusBg = 'bg-green-700'
  if (isDraft) { statusBg = 'bg-gray-900' }
  if (lesson.publication_status === 'completed') { statusBg = 'bg-clcnavy' }

  const body = (
    <div className="flex items-center gap-4 p-5">
      <div className="flex-shrink-0 h-14 w-14 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
        <div className={`text-xs font-medium uppercase tracking-wider text-white py-1 text-center ${statusBg}`}>{dayOfWeek}</div>
        <div className="bg-white dark:bg-slate-800 text-center text-lg font-medium text-slate-900 dark:text-slate-100">{dayOfMonth}</div>
      </div>
      <div className="flex flex-col min-w-0">
        <span className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-x-2">
          Lesson {isDraft && <span className='text-red-600 font-semibold'>Draft</span>}
        </span>
        <span className="text-base font-bold text-slate-900 dark:text-slate-100 leading-snug truncate group-hover:text-clcpurple dark:group-hover:text-clcpurple-light transition-colors">{lesson.name}</span>
        <div className="flex items-center gap-1.5 mt-1 text-sm text-slate-500 dark:text-slate-400">
          <ClockIcon className="w-4 h-4 flex-shrink-0" />
          <span className="truncate">{publishedAt}</span>
        </div>
      </div>
    </div>
  )

  if (isDraft) {
    return <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-md shadow-slate-900/[0.05] opacity-60">
      {body}
    </div>
  }

  return <a href={`/lessons/${lesson.id}`} className="group block bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-md shadow-slate-900/[0.05] hover:shadow-xl hover:shadow-clcpurple/20 hover:border-clcpurple/40 hover:-translate-y-0.5 hover:no-underline transition-all duration-300">
    {body}
  </a>
}

const CourseIndex = ({courseIdOverride}) => {
  const [ currentUser ] = useGlobalState('currentUser')
  const [ , setCurrentCourse ] = useGlobalState('currentCourse')
  const { id } = useParams()
  const [ course, setCourse ] = useState(null)
  const [ contents, setContents ] = useState([])
  const [ cohorts, setCohorts ] = useState([])
  const [ lessons, setLessons ] = useState([])
  const [ upcomingUserCohorts, setUpcomingUserCohorts ] = useState([])
  const [ completedUserCohorts, setCompletedUserCohorts ] = useState([])
  const [ events, setEvents ] = useState([])
  const [ attendingEventIds, setAttendingEventIds ] = useState([])
  const [ loading, setLoading ] = useState(false)
  const isComponentMounted = useRef(false)
  const courseId = id
  const { putpostRequest, getRequest } = useQuery()

  const join = () => { document.location.href = '/select_course' }

  useEffect(() => {
    setLoading(true)
    getRequest(`/api/v1/courses/${courseId}`, {}, (err, jsonData) => {
      if (err) { /* handled in hook */ return }
      setCourse(jsonData.course)
      setCurrentCourse(jsonData.course)
      setContents(jsonData.contents)
      setCohorts(jsonData.cohorts)
      setEvents(jsonData.events)
      setUpcomingUserCohorts(jsonData.upcomingUserCohorts)
      setCompletedUserCohorts(jsonData.completedUserCohorts)
      setAttendingEventIds(jsonData.attendingEventIds)
      setLessons(jsonData.lessons)
      setLoading(false)
    })
    isComponentMounted.current = true
  }, [courseId])

  if (!isComponentMounted.current) { return <Loading /> }
  if (loading) { return <Loading /> }

  return <div className='py-8 space-y-8'>

    <Link
      to={`/c/${courseId}/chat`}
      className='group flex items-center justify-between gap-4 bg-gradient-to-br from-clcpurple to-clcpink rounded-2xl p-5 sm:p-6 shadow-xl shadow-clcpurple/20 hover:shadow-clcpurple/40 hover:-translate-y-0.5 transition-all duration-300 hover:no-underline'
    >
      <div className='flex items-center gap-4 min-w-0'>
        <div className='flex-shrink-0 h-12 w-12 rounded-xl bg-white/20 flex items-center justify-center'>
          <svg className='h-6 w-6 text-white' viewBox='0 0 20 20' fill='currentColor' aria-hidden='true'>
            <path fillRule='evenodd' d='M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h2v2H9V8zm6 0h-2v2h2V8z' clipRule='evenodd' />
          </svg>
        </div>
        <div className='min-w-0'>
          <h2 className='text-lg sm:text-xl font-bold text-white leading-tight'>Course Chat</h2>
          <p className='text-sm text-white/80 truncate'>Join the conversation with teachers and classmates.</p>
        </div>
      </div>
      <svg className='h-5 w-5 text-white flex-shrink-0 transition-transform group-hover:translate-x-1' viewBox='0 0 20 20' fill='currentColor' aria-hidden='true'>
        <path fillRule='evenodd' d='M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z' clipRule='evenodd' />
      </svg>
    </Link>

    {upcomingUserCohorts.length > 0 && <section className={SECTION_CLASS}>
      <h2 className={H2_CLASS}>You're enrolled in {course.name}</h2>
      <div className="mt-6 space-y-4">
        {upcomingUserCohorts.map((cohort) => (
          <CohortRow key={`c${cohort.id}`} cohort={cohort} alreadyEnrolled={cohort.alreadyEnrolled} alreadyTaken={cohort.alreadyTaken} />
        ))}
      </div>
    </section>}

    {cohorts.length > 0 && <section className={SECTION_CLASS}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className={H2_CLASS}>Upcoming {course.name} Courses</h2>
        <PrimaryButton key='continue-button' onClick={join} text='Continue Enrollment' />
      </div>
      <div className="mt-6 space-y-4">
        {cohorts.map((cohort) => (
          <CohortRow key={`c${cohort.id}`} cohort={cohort} alreadyEnrolled={cohort.alreadyEnrolled} alreadyTaken={cohort.alreadyTaken} />
        ))}
      </div>
    </section>}

    {completedUserCohorts.length > 0 && <section className={SECTION_CLASS}>
      <h2 className={H2_CLASS}>Completed {course.name} Courses</h2>
      <div className="mt-6 space-y-3">
        {completedUserCohorts.map((uc) => (
          <a
            key={`complucc${uc.id}`}
            target="_blank"
            rel="noopener noreferrer"
            href={`https://www.closingcredits.com/certificates/${uc.publicToken || uc.id}`}
            className="inline-flex items-center gap-2 text-sm font-bold text-clcblue dark:text-clcnavy-light hover:text-clcnavy-dark transition-colors"
          >
            Download Letter of Completion
            <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M4.5 2A1.5 1.5 0 003 3.5v13A1.5 1.5 0 004.5 18h11a1.5 1.5 0 001.5-1.5V7.621a1.5 1.5 0 00-.44-1.06l-4.12-4.122A1.5 1.5 0 0011.378 2H4.5zM10 8a.75.75 0 01.75.75v3.69l1.22-1.22a.75.75 0 111.06 1.06l-2.5 2.5a.75.75 0 01-1.06 0l-2.5-2.5a.75.75 0 111.06-1.06l1.22 1.22V8.75A.75.75 0 0110 8z" clipRule="evenodd" /></svg>
          </a>
        ))}
      </div>
    </section>}

    {events.length > 0 && <section className={SECTION_CLASS}>
      <h2 className={H2_CLASS}>Class session events</h2>
      <p className='mt-2 text-sm text-slate-500 dark:text-slate-400'>
        Other Closing Credits events are on the <Link to='/events' className="font-bold text-clcblue dark:text-clcnavy-light hover:text-clcnavy-dark">Events page</Link>.
      </p>
      <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {events.map((event) => (
          <EventRow compact={true} key={`event${event.id}`} event={event} user={currentUser} attendingEventIds={attendingEventIds} />
        ))}
      </div>
    </section>}

    {lessons.length > 0 && <section className={SECTION_CLASS}>
      <h2 className={H2_CLASS}>Course lessons</h2>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {lessons.map((lesson) => (
          <LessonRow key={`teaccoh${lesson.id}`} lesson={lesson} currentUser={currentUser} />
        ))}
      </div>
    </section>}

  </div>
}

export default CourseIndex
